/**
 * AllTasksView - displays tasks across ALL boards in the organization
 * Used at the top-level projects list to show all tasks
 */

import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  format,
  isSameDay,
  differenceInDays,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
  isSameMonth,
} from 'date-fns';
import { tasksApi, boardsApi, projectsApi, type Task, type Board, type Project } from '@/services/api';
import { useAuthStore } from '@/stores/authStore';

type ViewMode = 'list' | 'timeline' | 'table';
type SortField = 'title' | 'projectId' | 'boardId' | 'priority' | 'dueDate' | 'createdAt';
type SortDirection = 'asc' | 'desc';

interface AllTasksViewProps {
  viewMode: ViewMode;
  searchQuery?: string;
  filterPriority?: string;
}

const priorityOrder: Record<string, number> = {
  urgent: 0,
  high: 1,
  medium: 2,
  low: 3,
  none: 4,
};

const priorityColors: Record<string, string> = {
  urgent: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200',
  high: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200',
  medium: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200',
  low: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200',
  none: 'bg-neutral-100 dark:bg-neutral-700 text-neutral-800 dark:text-neutral-200',
};

const priorityTimelineColors: Record<string, string> = {
  urgent: '#EF4444',
  high: '#F97316',
  medium: '#EAB308',
  low: '#22C55E',
  none: '#9CA3AF',
};

// Extended task with project info
interface TaskWithProject extends Task {
  projectId?: string;
}

export function AllTasksView({
  viewMode,
  searchQuery = '',
  filterPriority = '',
}: AllTasksViewProps) {
  const navigate = useNavigate();
  const { currentOrgId } = useAuthStore();
  const [tasks, setTasks] = useState<TaskWithProject[]>([]);
  const [boards, setBoards] = useState<Board[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // List/Table view state
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());

  // Timeline view state
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [timelineViewMode, setTimelineViewMode] = useState<'month' | 'week'>('month');

  // Fetch all tasks across all boards
  const fetchAllTasks = useCallback(async () => {
    if (!currentOrgId) return;

    setIsLoading(true);
    setError(null);

    try {
      // First fetch all projects for the organization
      const projectsResponse = await projectsApi.list(currentOrgId);
      const allProjects = projectsResponse.items;
      setProjects(allProjects);

      // Build board-to-project mapping
      const allBoards: Board[] = [];
      const boardToProject: Record<string, string> = {};

      for (const project of allProjects) {
        const boardsResponse = await boardsApi.list(project.id);
        for (const board of boardsResponse.items) {
          allBoards.push(board);
          boardToProject[board.id] = project.id;
        }
      }
      setBoards(allBoards);

      // Then fetch tasks for each board
      const allTasks: TaskWithProject[] = [];
      for (const board of allBoards) {
        let page = 1;
        let hasMore = true;
        while (hasMore) {
          const response = await tasksApi.list({ boardId: board.id }, page, 100);
          for (const task of response.items) {
            const projectId = boardToProject[task.boardId];
            const taskWithProject: TaskWithProject = { ...task };
            if (projectId) {
              taskWithProject.projectId = projectId;
            }
            allTasks.push(taskWithProject);
          }
          hasMore = response.hasMore;
          page++;
        }
      }
      setTasks(allTasks);
    } catch (err) {
      console.error('Failed to fetch tasks:', err);
      setError('Failed to load tasks');
    } finally {
      setIsLoading(false);
    }
  }, [currentOrgId]);

  useEffect(() => {
    void fetchAllTasks();
  }, [fetchAllTasks]);

  // Filter tasks (exclude completed)
  const filteredTasks = useMemo(() => {
    return tasks.filter((task) => {
      // Hide completed tasks
      if (task.isCompleted) return false;
      if (filterPriority && task.priority !== filterPriority) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          task.title.toLowerCase().includes(query) ||
          task.taskKey.toLowerCase().includes(query) ||
          task.description?.toLowerCase().includes(query)
        );
      }
      return true;
    });
  }, [tasks, filterPriority, searchQuery]);

  // Sort tasks
  const sortedTasks = useMemo(() => {
    return [...filteredTasks].sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'title':
          comparison = a.title.localeCompare(b.title);
          break;
        case 'projectId':
          comparison = (a.projectId ?? '').localeCompare(b.projectId ?? '');
          break;
        case 'boardId':
          comparison = a.boardId.localeCompare(b.boardId);
          break;
        case 'priority':
          comparison =
            (priorityOrder[a.priority] ?? 4) - (priorityOrder[b.priority] ?? 4);
          break;
        case 'dueDate': {
          const dateA = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
          const dateB = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
          comparison = dateA - dateB;
          break;
        }
        case 'createdAt':
          comparison =
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [filteredTasks, sortField, sortDirection]);

  // Get board name by ID
  const getBoardName = (boardId: string): string => {
    const board = boards.find((b) => b.id === boardId);
    return board?.name ?? 'Unknown';
  };

  // Get project name by ID
  const getProjectName = (projectId?: string): string => {
    if (!projectId) return 'Unknown';
    const project = projects.find((p) => p.id === projectId);
    return project?.name ?? 'Unknown';
  };

  // Handle task click - navigate to board with task selected
  const handleTaskClick = (task: Task) => {
    navigate(`/boards/${task.boardId}?task=${task.id}`);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Timeline helpers
  const dateRange = useMemo(() => {
    if (timelineViewMode === 'week') {
      return {
        start: startOfWeek(currentMonth, { weekStartsOn: 1 }),
        end: endOfWeek(currentMonth, { weekStartsOn: 1 }),
      };
    }
    return {
      start: startOfMonth(currentMonth),
      end: endOfMonth(currentMonth),
    };
  }, [currentMonth, timelineViewMode]);

  const days = useMemo(() => eachDayOfInterval(dateRange), [dateRange]);
  const today = new Date();

  const tasksWithDates = useMemo(() => {
    return sortedTasks
      .filter((task) => task.startDate || task.dueDate)
      .sort((a, b) => {
        const dateA = a.startDate ?? a.dueDate ?? '';
        const dateB = b.startDate ?? b.dueDate ?? '';
        return dateA.localeCompare(dateB);
      });
  }, [sortedTasks]);

  const getTaskPosition = (task: Task) => {
    const startDate = task.startDate
      ? new Date(task.startDate)
      : task.dueDate
        ? new Date(task.dueDate)
        : null;
    const endDate = task.dueDate
      ? new Date(task.dueDate)
      : task.startDate
        ? new Date(task.startDate)
        : null;

    if (!startDate || !endDate) return null;

    const rangeStart = dateRange.start;
    const rangeEnd = dateRange.end;
    const totalDays = differenceInDays(rangeEnd, rangeStart) + 1;

    if (endDate < rangeStart || startDate > rangeEnd) return null;

    const effectiveStart = startDate < rangeStart ? rangeStart : startDate;
    const effectiveEnd = endDate > rangeEnd ? rangeEnd : endDate;

    const startOffset = differenceInDays(effectiveStart, rangeStart);
    const duration = differenceInDays(effectiveEnd, effectiveStart) + 1;

    return {
      left: (startOffset / totalDays) * 100,
      width: (duration / totalDays) * 100,
    };
  };

  const toggleRowSelection = (taskId: string) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  };

  const toggleAllRows = () => {
    if (selectedRows.size === sortedTasks.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(sortedTasks.map((t) => t.id)));
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return (
        <svg className="w-4 h-4 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return sortDirection === 'asc' ? (
      <svg className="w-4 h-4 text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg bg-red-100 p-4 text-red-700 dark:bg-red-900/30 dark:text-red-400">
        {error}
      </div>
    );
  }

  // LIST VIEW
  if (viewMode === 'list') {
    return (
      <div className="h-full flex flex-col bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
        <div className="p-4 border-b border-neutral-200 dark:border-neutral-700">
          <span className="text-sm text-neutral-500 dark:text-neutral-400">
            {sortedTasks.length} task{sortedTasks.length !== 1 ? 's' : ''} across {boards.length} board{boards.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex-1 overflow-auto">
          <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-700">
            <thead className="bg-neutral-50 dark:bg-neutral-900 sticky top-0">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-800"
                  onClick={() => handleSort('title')}
                >
                  <div className="flex items-center gap-2">Task <SortIcon field="title" /></div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-800"
                  onClick={() => handleSort('projectId')}
                >
                  <div className="flex items-center gap-2">Project <SortIcon field="projectId" /></div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-800"
                  onClick={() => handleSort('boardId')}
                >
                  <div className="flex items-center gap-2">Board <SortIcon field="boardId" /></div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-800"
                  onClick={() => handleSort('priority')}
                >
                  <div className="flex items-center gap-2">Priority <SortIcon field="priority" /></div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-800"
                  onClick={() => handleSort('dueDate')}
                >
                  <div className="flex items-center gap-2">Due Date <SortIcon field="dueDate" /></div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-neutral-800 divide-y divide-neutral-200 dark:divide-neutral-700">
              {sortedTasks.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-neutral-500 dark:text-neutral-400">
                    No tasks found
                  </td>
                </tr>
              ) : (
                sortedTasks.map((task) => (
                  <tr
                    key={task.id}
                    onClick={() => handleTaskClick(task)}
                    className="hover:bg-neutral-50 dark:hover:bg-neutral-700 cursor-pointer"
                  >
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-neutral-900 dark:text-white">{task.title}</div>
                      <div className="text-xs text-neutral-500 dark:text-neutral-400">{task.taskKey}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-200">
                        {getProjectName(task.projectId)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-neutral-100 dark:bg-neutral-700 text-neutral-800 dark:text-neutral-200">
                        {getBoardName(task.boardId)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${priorityColors[task.priority] ?? priorityColors.none}`}>
                        {task.priority}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500 dark:text-neutral-400">
                      {task.dueDate ? new Date(task.dueDate).toLocaleDateString() : '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // TIMELINE VIEW
  if (viewMode === 'timeline') {
    return (
      <div className="h-full flex flex-col bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
        <div className="p-4 border-b border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentMonth((prev) => timelineViewMode === 'week' ? subMonths(prev, 0.25) : subMonths(prev, 1))}
                className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-md text-neutral-600 dark:text-neutral-400"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={() => setCurrentMonth(new Date())}
                className="px-3 py-1 text-sm font-medium text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-md"
              >
                Today
              </button>
              <button
                onClick={() => setCurrentMonth((prev) => timelineViewMode === 'week' ? addMonths(prev, 0.25) : addMonths(prev, 1))}
                className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-md text-neutral-600 dark:text-neutral-400"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
              {format(currentMonth, 'MMMM yyyy')}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setTimelineViewMode('week')}
              className={`px-3 py-1 text-sm rounded-md ${timelineViewMode === 'week' ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300' : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700'}`}
            >
              Week
            </button>
            <button
              onClick={() => setTimelineViewMode('month')}
              className={`px-3 py-1 text-sm rounded-md ${timelineViewMode === 'month' ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300' : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700'}`}
            >
              Month
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto">
          <div className="min-w-[800px]">
            <div className="flex border-b border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 sticky top-0 z-10">
              <div className="w-64 flex-shrink-0 px-4 py-2 border-r border-neutral-200 dark:border-neutral-700 font-medium text-sm text-neutral-700 dark:text-neutral-300">
                Task
              </div>
              <div className="flex-1 flex">
                {days.map((day) => (
                  <div
                    key={day.toISOString()}
                    className={`flex-1 text-center py-2 text-xs border-r border-neutral-200 dark:border-neutral-700 ${isSameDay(day, today) ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium' : !isSameMonth(day, currentMonth) ? 'text-neutral-400 dark:text-neutral-500' : 'text-neutral-600 dark:text-neutral-400'}`}
                  >
                    <div>{format(day, 'EEE')}</div>
                    <div className="font-medium">{format(day, 'd')}</div>
                  </div>
                ))}
              </div>
            </div>
            {tasksWithDates.length === 0 ? (
              <div className="p-12 text-center text-neutral-500 dark:text-neutral-400">
                No tasks with dates in this range
              </div>
            ) : (
              tasksWithDates.map((task) => {
                const position = getTaskPosition(task);
                return (
                  <div key={task.id} className="flex border-b border-neutral-200 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-700">
                    <div
                      className="w-64 flex-shrink-0 px-4 py-3 border-r border-neutral-200 dark:border-neutral-700 cursor-pointer"
                      onClick={() => handleTaskClick(task)}
                    >
                      <div className="text-sm font-medium text-neutral-900 dark:text-white truncate">{task.title}</div>
                      <div className="text-xs text-neutral-500 dark:text-neutral-400">{task.taskKey}</div>
                    </div>
                    <div className="flex-1 relative h-12">
                      <div className="absolute inset-0 flex">
                        {days.map((day) => (
                          <div
                            key={day.toISOString()}
                            className={`flex-1 border-r border-neutral-200 dark:border-neutral-700 ${isSameDay(day, today) ? 'bg-primary-50 dark:bg-primary-900/20' : ''}`}
                          />
                        ))}
                      </div>
                      {position && (
                        <div
                          className="absolute top-2 h-8 rounded-md cursor-pointer hover:opacity-80 transition-opacity flex items-center px-2"
                          style={{
                            left: `${position.left}%`,
                            width: `${Math.max(position.width, 2)}%`,
                            backgroundColor: priorityTimelineColors[task.priority] ?? priorityTimelineColors.none,
                          }}
                          onClick={() => handleTaskClick(task)}
                        >
                          <span className="text-xs text-white font-medium truncate">{task.title}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
        <div className="p-4 border-t border-neutral-200 dark:border-neutral-700 flex items-center gap-6 text-sm">
          <span className="text-neutral-500 dark:text-neutral-400">Priority:</span>
          {Object.entries(priorityTimelineColors).map(([priority, color]) => (
            <div key={priority} className="flex items-center gap-1">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: color }} />
              <span className="capitalize text-neutral-600 dark:text-neutral-400">{priority}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // TABLE VIEW
  return (
    <div className="h-full flex flex-col bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
      <div className="p-4 border-b border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-sm text-neutral-500 dark:text-neutral-400">
            {sortedTasks.length} task{sortedTasks.length !== 1 ? 's' : ''} across {boards.length} board{boards.length !== 1 ? 's' : ''}
          </span>
          {selectedRows.size > 0 && (
            <span className="text-sm text-primary-600 dark:text-primary-400">{selectedRows.size} selected</span>
          )}
        </div>
        {selectedRows.size > 0 && (
          <div className="flex items-center gap-2">
            <button className="px-3 py-1 text-sm text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-md">
              Bulk Edit
            </button>
            <button className="px-3 py-1 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md">
              Delete
            </button>
          </div>
        )}
      </div>
      <div className="flex-1 overflow-auto">
        <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-700">
          <thead className="bg-neutral-50 dark:bg-neutral-900 sticky top-0">
            <tr>
              <th className="w-12 px-4 py-3">
                <input
                  type="checkbox"
                  checked={selectedRows.size === sortedTasks.length && sortedTasks.length > 0}
                  onChange={toggleAllRows}
                  className="h-4 w-4 text-primary-600 rounded border-neutral-300 dark:border-neutral-600"
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Key</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider min-w-[300px]">Title</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Project</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Board</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Priority</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Due Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Type</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-neutral-800 divide-y divide-neutral-200 dark:divide-neutral-700">
            {sortedTasks.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center text-neutral-500 dark:text-neutral-400">
                  No tasks found
                </td>
              </tr>
            ) : (
              sortedTasks.map((task) => (
                <tr
                  key={task.id}
                  className={selectedRows.has(task.id) ? 'bg-primary-50 dark:bg-primary-900/20' : 'hover:bg-neutral-50 dark:hover:bg-neutral-700'}
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedRows.has(task.id)}
                      onChange={() => toggleRowSelection(task.id)}
                      className="h-4 w-4 text-primary-600 rounded border-neutral-300 dark:border-neutral-600"
                    />
                  </td>
                  <td
                    className="px-4 py-3 text-sm text-primary-600 dark:text-primary-400 font-medium cursor-pointer hover:underline"
                    onClick={() => handleTaskClick(task)}
                  >
                    {task.taskKey}
                  </td>
                  <td className="px-4 py-3 text-sm text-neutral-900 dark:text-white">{task.title}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-200">
                      {getProjectName(task.projectId)}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-neutral-100 dark:bg-neutral-700 text-neutral-800 dark:text-neutral-200">
                      {getBoardName(task.boardId)}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${priorityColors[task.priority] ?? priorityColors.none}`}>
                      {task.priority}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-neutral-500 dark:text-neutral-400">
                    {task.dueDate ? new Date(task.dueDate).toLocaleDateString() : '-'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-neutral-500 dark:text-neutral-400 capitalize">
                    {task.taskType}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
